import React, { useState, useMemo } from 'react';
import { ArrowLeftIcon, FunnelIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { useLocation } from '../contexts/LocationContext';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from '../hooks/useApi';
import LoadingSpinner from '../components/UI/LoadingSpinner';

interface FishSamplingLog {
  id: string;
  pond_id: string;
  sampling_date: string;
  sample_weight_kg: number;
  sample_count: number;
  avg_weight_g?: number;
  estimated_total_biomass_kg?: number;
  remarks?: string;
  created_at: string;
  created_by: string;
  pond_name?: string;
  fish_species?: string[] | string;
  species_breakdown?: { [key: string]: { weight_kg: number; count: number } };
}

const FishSamplingLogs: React.FC = () => {
  const { selectedLocation } = useLocation();
  const { user } = useAuth();

  const getDefaultDates = () => {
    const today = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(today.getDate() - 7);
    return {
      startDate: sevenDaysAgo.toISOString().split('T')[0],
      endDate: today.toISOString().split('T')[0]
    };
  };
  const defaults = getDefaultDates();
  const [startDate, setStartDate] = useState(defaults.startDate);
  const [endDate, setEndDate] = useState(defaults.endDate);
  const [selectedPond, setSelectedPond] = useState('');

  const buildEndpoint = () => {
    if (!selectedLocation) return '';
    let endpoint = `/locations/${selectedLocation.id}/fish-sampling-logs`;
    const params: string[] = [];
    if (startDate) params.push(`start_date=${startDate}`);
    if (endDate) params.push(`end_date=${endDate}`);
    if (selectedPond) params.push(`pond_id=${selectedPond}`);
    if (params.length > 0) endpoint += `?${params.join('&')}`;
    return endpoint;
  };

  const { data: pondsData } = useApi(`/locations/${selectedLocation?.id}/ponds`);
  const { data: logs, loading, error, refetch } = useApi<FishSamplingLog[]>(buildEndpoint(), {
    dependencies: [selectedLocation?.id, startDate, endDate, selectedPond],
    skip: !selectedLocation
  });

  const enrichedLogs = useMemo(() => {
    if (!logs || !pondsData) return [] as FishSamplingLog[];
    return (logs as any[]).map((log: any) => ({
      ...log,
      pond_name: (pondsData as any[]).find((p: any) => p.id === log.pond_id)?.name || 'Unknown Pond'
    }));
  }, [logs, pondsData]);

  const clearFilters = () => {
    const d = getDefaultDates();
    setStartDate(d.startDate);
    setEndDate(d.endDate);
    setSelectedPond('');
  };

  const fmtDate = (d: string) => new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

  if (!selectedLocation) {
    return (
      <div className="max-w-md mx-auto bg-gray-50 min-h-screen py-8 text-center">
        <p className="text-gray-600">Please select a location first</p>
        <Link to="/locations" className="text-blue-600 hover:text-blue-800">Go to Location Selection</Link>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto bg-gray-50 min-h-screen">
      <div className="bg-white shadow-sm p-4 sticky top-0 z-10">
        <div className="flex items-center gap-2 mb-3">
          <Link to="/data-entry" className="p-1 text-gray-400 hover:text-gray-600"><ArrowLeftIcon className="h-5 w-5" /></Link>
          <h1 className="text-lg font-bold text-gray-800">📊 Fish Sampling Logs</h1>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2"><FunnelIcon className="h-4 w-4 text-gray-500" /><span className="text-sm font-medium text-gray-700">Filters</span></div>
            <div className="text-xs text-purple-600 bg-purple-50 px-2 py-1 rounded">Last 7 days</div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">From Date</label>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-full p-2 border border-gray-300 rounded-lg text-xs" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">To Date</label>
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-full p-2 border border-gray-300 rounded-lg text-xs" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Pond</label>
              <select value={selectedPond} onChange={(e) => setSelectedPond(e.target.value)} className="w-full p-2 border border-gray-300 rounded-lg text-xs">
                <option value="">All Ponds</option>
                {(pondsData as any[] || []).map((pond: any) => (
                  <option key={pond.id} value={pond.id}>{pond.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex gap-2">
            <button onClick={clearFilters} className="flex-1 bg-gray-100 text-gray-700 py-2 px-3 rounded-lg text-xs font-medium hover:bg-gray-200 transition-colors">Reset to 7 Days</button>
            <button onClick={refetch} className="flex-1 bg-purple-600 text-white py-2 px-3 rounded-lg text-xs font-medium hover:bg-purple-700 transition-colors">Refresh</button>
          </div>
        </div>
      </div>

      {error && (<div className="p-4 bg-red-50 border border-red-200 rounded-lg m-4"><p className="text-red-800 text-sm">{error}</p></div>)}
      {loading && (<div className="p-8 text-center"><LoadingSpinner size="md" /><p className="text-gray-500 mt-2">Loading fish sampling logs...</p></div>)}

      {!loading && (
        <div className="p-4 space-y-3">
          {(enrichedLogs as any[]).length === 0 ? (
            <div className="text-center py-8">
              <div className="text-4xl mb-4">📊</div>
              <p className="text-gray-500 mb-2">No fish sampling logs found</p>
              <p className="text-sm text-gray-400">Try adjusting your filters or add new entries.</p>
              {user?.role !== 'user' && (
                <Link to="/data-entry/sampling" className="inline-block mt-4 bg-purple-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-purple-700 transition-colors">Add Fish Sampling</Link>
              )}
            </div>
          ) : (
            (enrichedLogs as any[]).map((log: any) => (
              <div key={log.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="flex items-start justify-between mb-1">
                  <div>
                    <h3 className="font-semibold text-gray-800">{log.pond_name}</h3>
                    <p className="text-sm text-gray-500">{fmtDate(log.sampling_date)}</p>
                  </div>
                  <div className="text-right text-xs text-gray-600">
                    <div>Sample: <span className="font-semibold">{log.sample_weight_kg}</span> kg / <span className="font-semibold">{log.sample_count}</span></div>
                    {log.avg_weight_g !== undefined && <div>Avg: <span className="font-semibold">{log.avg_weight_g}</span> g</div>}
                  </div>
                </div>
                {/* Species breakdown (if multi-species) */}
                {log.species_breakdown && (
                  <div className="mt-2 bg-gray-50 border border-gray-100 rounded p-2">
                    <div className="text-[11px] text-gray-600 mb-1">Species breakdown</div>
                    <div className="grid grid-cols-2 gap-1">
                      {Object.entries(log.species_breakdown).map(([species, v]: any) => (
                        <div key={species} className="text-[11px] text-gray-700 flex items-center justify-between">
                          <span className="capitalize mr-2">{species.replace('_', ' ')}</span>
                          <span className="text-gray-600">{v.weight_kg} kg / {v.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {log.remarks && <div className="text-xs text-gray-700">Remarks: {log.remarks}</div>}
              </div>
            ))
          )}
        </div>
      )}

      {user?.role !== 'user' && (
        <div className="fixed bottom-6 right-6">
          <Link to="/data-entry/sampling" className="bg-purple-600 text-white p-4 rounded-full shadow-lg hover:bg-purple-700 transition-colors flex items-center gap-2">
            <span className="text-xl">📊</span>
            <span className="text-sm font-medium">Add Sampling</span>
          </Link>
        </div>
      )}
    </div>
  );
};

export default FishSamplingLogs;


